import { PropsWithChildren, useEffect, useState } from 'react';

import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { useAuth0 } from '@auth0/auth0-react';
import { AuthOptions, createAuthLink } from 'aws-appsync-auth-link';
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link';

import {
  VITE_APPSYNC_API,
  VITE_NOTIFICATIONS_FALLBACK_REFRESH_ENABLED,
} from '~/lib/constants/env';

import { auth0SilentTokenOptions } from '../constants/global.constants';
import { useDatadog } from '~/lib/hooks/useDatadog';
import { isInvalidRefreshTokenError } from '~/lib/util/auth.util';

const AppSyncContextProvider = ({ children }: PropsWithChildren) => {
  const { getAccessTokenSilently, logout } = useAuth0();
  const { logError } = useDatadog();

  const [client, setClient] =
    useState<ApolloClient<NormalizedCacheObject> | null>(null);

  const initApolloClient = () => {
    console.log('initApolloClient - time: ', new Date().toISOString());
    const auth: AuthOptions = {
      jwtToken: async () => {
        try {
          return await getAccessTokenSilently(auth0SilentTokenOptions);
        } catch (e) {
          if (isInvalidRefreshTokenError(e)) {
            // Refresh token was rotated by another tab — cannot retry.
            // Redirect to login so the user can re-authenticate.
            await logout({ logoutParams: { returnTo: globalThis.location.origin } });
            return '';
          }
          logError(e, {
            message: 'Error getting token silently while initApolloClient',
          });
          return '';
        }
      },
      type: 'OPENID_CONNECT',
    };

    const authLinkOptions = {
      auth,
      region: null,
      url: VITE_APPSYNC_API,
    };

    const httpLink = new HttpLink({ uri: VITE_APPSYNC_API });

    const errorLink = onError(({ graphQLErrors, networkError }) => {
      if (graphQLErrors)
        graphQLErrors.forEach(({ message, locations, path }) => {
          console.log(
            `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
          );
          logError(
            new Error(
              `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
            ),
          );
        });
      if (networkError) {
        console.log(
          `[Network error]: ${networkError && JSON.stringify(networkError)}`,
        );
        logError(networkError);
      }
    });

    const link = ApolloLink.from([
      errorLink,
      createAuthLink(authLinkOptions),
      createSubscriptionHandshakeLink(authLinkOptions, httpLink),
    ]);

    const apolloClient = new ApolloClient({
      cache: new InMemoryCache(),
      link,
    });

    setClient(apolloClient);
  };

  useEffect(() => {
    if (!client) {
      initApolloClient();
    }

    // Initializing a new ApolloClient instance every 5 minutes as a workaround for
    // https://teamequip.atlassian.net/browse/EQ-12869
    let interval: ReturnType<typeof setInterval>;

    if (VITE_NOTIFICATIONS_FALLBACK_REFRESH_ENABLED === 'true') {
      interval = setInterval(() => {
        initApolloClient();
      }, 300000);
    }

    return () => clearInterval(interval);
  }, []);

  return client && <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default AppSyncContextProvider;
